import type { Card } from '../trick/cards.js';
import { sameCard } from '../trick/cards.js';
import { QUEEN_OF_SPADES, cardHeartsPoints } from './play.js';
import type { HeartsView } from './view.js';

/** `easy` passes and plays at random. `clever` passes its dangerous cards, ducks tricks and dumps points when it is void. */
export type HeartsRobotLevel = 'easy' | 'clever';

const lowest = (cards: readonly Card[]): Card => cards.reduce((a, b) => (b.rank < a.rank ? b : a));
const highest = (cards: readonly Card[]): Card => cards.reduce((a, b) => (b.rank > a.rank ? b : a));

/** How much a card is worth getting rid of: the queen and the high spades above her, high hearts, then any high card. */
function danger(c: Card, spades: number): number {
  if (sameCard(c, QUEEN_OF_SPADES)) return 100;
  if (c.suit === 'S' && c.rank > 12) return spades <= 4 ? 90 + c.rank : c.rank; // king and ace of spades are risky unless spades are plentiful to protect them
  if (c.suit === 'H') return 40 + c.rank;
  return c.rank;
}

/** The cards to pass (three, or two with Big Cards). Easy picks any three; clever passes the most dangerous. */
export function heartsChoosePass(view: HeartsView, level: HeartsRobotLevel, random: () => number): Card[] {
  const hand = [...view.hand];
  if (level === 'easy') {
    const picks: Card[] = [];
    const pool = [...hand];
    while (picks.length < view.passCount) picks.push(pool.splice(Math.floor(random() * pool.length), 1)[0] as Card);
    return picks;
  }
  const spades = hand.filter((c) => c.suit === 'S').length;
  return [...hand].sort((a, b) => danger(b, spades) - danger(a, spades)).slice(0, view.passCount);
}

/** The card a robot plays; it sees only its own `HeartsView`. `random` is used by the easy robot only. */
export function heartsChooseCard(view: HeartsView, level: HeartsRobotLevel, random: () => number): Card {
  const legal = view.legalPlays;
  if (legal.length === 0) throw new Error("heartsChooseCard: nothing to play (not this seat's turn?)");
  if (legal.length === 1) return legal[0] as Card;
  if (level === 'easy') return legal[Math.floor(random() * legal.length)] as Card;

  let card: Card;
  if (view.trick.length === 0) {
    // Lead low and safe: the lowest card, preferring a suit that is not hearts and not spades (keep the queen buried).
    const safe = legal.filter((c) => c.suit !== 'H' && c.suit !== 'S');
    card = lowest(safe.length > 0 ? safe : legal);
  } else {
    const led = view.trick[0]?.card.suit;
    const following = legal.filter((c) => c.suit === led);
    if (following.length > 0) {
      const highestLed = Math.max(...view.trick.filter((p) => p.card.suit === led).map((p) => p.card.rank)); // the card now winning
      const ducking = following.filter((c) => c.rank < highestLed);
      const pointsInTrick = view.trick.reduce((a, p) => a + cardHeartsPoints(p.card, view.minRank), 0);
      if (ducking.length > 0) card = highest(ducking); // lose the trick with the highest card that still loses
      else if (view.trick.length === 3 && pointsInTrick === 0) card = highest(following); // last to play and nothing to take: winning is free
      else card = lowest(following); // forced to win: give up as little as possible
    } else {
      // Void: dump the queen of spades, else the highest heart, else the highest card.
      const queen = legal.find((c) => sameCard(c, QUEEN_OF_SPADES));
      const hearts = legal.filter((c) => c.suit === 'H');
      card = queen ?? (hearts.length > 0 ? highest(hearts) : highest(legal));
    }
  }
  return legal.find((c) => sameCard(c, card)) ?? (legal[0] as Card);
}
